import { useState, useEffect } from 'react'
import { XMarkIcon, UsersIcon } from '@heroicons/react/24/outline'
import { TeamService } from '../../../../shared/services/teamService'
import { FolderService } from '../../../../shared/services/folderService'
import {
  folderApi,
  type FolderSummary,
} from '../../../../shared/services/folderApi'
import { useTranslation } from 'react-i18next'

interface TeamOption {
  _id: string
  name: string
  userRole: string
}

interface ShareFolderModalProps {
  folderId: string
  onClose: () => void
  onDone?: () => void
}

interface CurrentShare {
  name: string
  isShared: boolean
  teamId: string | null
  allowTeamEdit: boolean
}

function ShareFolderModal({
  folderId,
  onClose,
  onDone,
}: ShareFolderModalProps) {
  const { t } = useTranslation()
  const [teams, setTeams] = useState<TeamOption[]>([])
  const [current, setCurrent] = useState<CurrentShare | null>(null)
  const [selectedTeam, setSelectedTeam] = useState('')
  const [allowTeamEdit, setAllowTeamEdit] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        const [teamsRes, folders] = await Promise.all([
          TeamService.getTeams(),
          folderApi.list(),
        ])
        if (!active) {
          return
        }

        const teamList = (teamsRes?.success
          ? (teamsRes.teams ?? [])
          : []) as unknown as TeamOption[]
        setTeams(teamList)

        const folder = folders.find(item => item._id === folderId) as
          | (FolderSummary & {
              isShared?: boolean
              teamId?: string | null
              shareSettings?: { allowTeamEdit?: boolean }
            })
          | undefined
        if (folder) {
          const share: CurrentShare = {
            name: folder.name,
            isShared: !!folder.isShared,
            teamId: folder.teamId ? String(folder.teamId) : null,
            allowTeamEdit: folder.shareSettings?.allowTeamEdit ?? true,
          }
          setCurrent(share)
          setSelectedTeam(share.teamId || teamList[0]?._id || '')
          setAllowTeamEdit(share.allowTeamEdit)
        } else {
          setSelectedTeam(teamList[0]?._id || '')
        }
      } catch (err) {
        if (active) {
          setError(t('form.shareLoadError'))
        }
        console.error('공유 정보 로드 실패:', err)
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }
    load()
    return () => {
      active = false
    }
  }, [folderId])

  const handleShare = async () => {
    if (!selectedTeam) {
      setError(t('form.selectTeam'))
      return
    }
    try {
      setSaving(true)
      setError(null)
      const res = await FolderService.shareFolder(folderId, {
        teamId: selectedTeam,
        allowTeamEdit,
      })
      if (!res?.success) {
        setError(res?.message || t('form.shareError'))
        return
      }
      onDone?.()
      onClose()
    } catch (err) {
      setError(t('form.shareError'))
      console.error('폴더 공유 실패:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleUnshare = async () => {
    try {
      setSaving(true)
      setError(null)
      const res = await FolderService.unshareFolder(folderId)
      if (!res?.success) {
        setError(res?.message || '공유 해제에 실패했습니다')
        return
      }
      onDone?.()
      onClose()
    } catch (err) {
      setError('공유 해제에 실패했습니다')
      console.error('공유 해제 실패:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
            <UsersIcon className="mr-2 h-5 w-5 text-blue-600" />
            {t('form.shareFolder')}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('form.loading')}
          </p>
        ) : teams.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('form.noTeams')}
          </p>
        ) : (
          <div className="space-y-4">
            {current?.name && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">{current.name}</span> 폴더를 팀과
                공유합니다.
              </p>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('form.shareTeam')}
              </label>
              <select
                value={selectedTeam}
                onChange={e => setSelectedTeam(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {teams.map(team => (
                  <option key={team._id} value={team._id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={allowTeamEdit}
                onChange={e => setAllowTeamEdit(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              팀 편집자(owner·editor)의 편집 허용
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex items-center justify-between gap-2 pt-2">
              {current?.isShared ? (
                <button
                  onClick={handleUnshare}
                  disabled={saving}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
                >
                  공유 해제
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={handleShare}
                disabled={saving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {current?.isShared
                  ? t('form.shareSave')
                  : t('form.shareSubmit')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ShareFolderModal
